import { isEqual } from 'lodash';
import { handleActions } from 'redux-actions';

import {
  deepFreeze,
  escapeSpecialCharacters,
  makeUniqueArray,
} from '@mw/helpers';

import {
  createChatAction,
  setCharCounter,
  setSelectedConversation,
  setMessagesSearch,
  setScrollToBottom,
  setFiltersOrder,
  setFiltersOpen,
  resetFilters,
  addConversations,
  setConversations,
  updateConversation,
  addChannelTypesToConversations,
  setFilterOnlyUnread,
} from '~/actions/conversations';

import { ChannelType } from '~/constants';

const getChannelNameByAgentID = ({ agentID, agents, channels }) => {
  const agent = agents.find((ag) => ag.id === agentID);

  if (!agent) return ''; // Handle case where agent is not found

  const channel = channels.find((ch) => ch.id === agent.channel_id);

  if (!channel) return ''; // Handle case where channel is not found

  const isSMSChannel = channel.type === ChannelType.SMS;

  return isSMSChannel ? agent.config.vln : channel.config.name || channel.type;
};

const createConversationReducer = (
  conversationID,
  agents = [],
  channels = []
) =>
  handleActions(
    {
      [createChatAction(conversationID, 'SET_MESSAGES')]: (
        state,
        { payload: { messages, count, limit, offset, total, hasUpdatedFile } }
      ) => ({
        ...state,
        limit,
        offset,
        total,
        messages: messages.map((message) => ({
          ...message,
          channelName: getChannelNameByAgentID({
            agentID: message.agent_id,
            agents,
            channels,
          }),
        })),
        count,
        hasUpdatedFile,
      }),
      [createChatAction(conversationID, 'RESET_MESSAGES')]: (state) => ({
        ...state,
        count: 0,
        messages: [],
        hasUpdatedFile: false,
      }),
      [`chats/${conversationID}/PROCESS_CHAR_COUNTER`]: (
        state,
        { payload }
      ) => ({
        ...state,
        ...payload,
      }),
    },
    {
      messages: [],
      count: 0,
      hasUpdatedFile: false,
    }
  );

const defaultState = deepFreeze({
  conversations: [],
  fetchCursor: undefined,
  selectedConversation: null,
  messagesSearch: '',
  charCounter: 0,
  scrollToBottom: () => {},
  filtersOpen: false,
  filtersAreChanged: false,
  filters: {
    order: -1,
    onlyUnread: false,
  },
});

const haveFiltersChanged = (filters) => !isEqual(filters, defaultState.filters);

const conversationsReducer = handleActions(
  {
    [setSelectedConversation]: (
      state,
      { payload: { selectedConversation } }
    ) => ({
      ...state,
      selectedConversation,
    }),
    [setMessagesSearch]: (state, { payload: { searchString } }) => {
      const formattedSearch = searchString
        ? escapeSpecialCharacters(searchString.toLowerCase())
        : '';

      return {
        ...state,
        messagesSearch: formattedSearch,
      };
    },
    [setCharCounter]: (state, { payload: { charCounter } }) => ({
      ...state,
      charCounter,
    }),
    [setFilterOnlyUnread]: (state, { payload: onlyUnread }) => {
      const filters = {
        ...state.filters,
        onlyUnread,
      };

      return {
        ...state,
        filtersAreChanged: haveFiltersChanged(filters),
        filters,
      };
    },
    [setFiltersOrder]: (state, { payload: sortOrder }) => {
      const filters = {
        ...state.filters,
        order: sortOrder,
      };

      return {
        ...state,
        filtersAreChanged: haveFiltersChanged(filters),
        filters,
      };
    },
    [setFiltersOpen]: (state, { payload: filtersOpen }) => ({
      ...state,
      filtersOpen,
    }),
    [resetFilters]: (state) => ({
      ...state,
      filters: defaultState.filters,
      filtersAreChanged: defaultState.filtersAreChanged,
    }),
    [setScrollToBottom]: (state, { payload: { scrollToBottom } }) => ({
      ...state,
      scrollToBottom,
    }),
    [setConversations]: (
      state,
      { payload: { conversations, fetchCursor } }
    ) => ({
      ...state,
      conversations,
      fetchCursor,
    }),
    [addConversations]: (
      state,
      { payload: { conversations, fetchCursor } }
    ) => {
      const updatedConversations = [...state.conversations];

      conversations.forEach((conversation) => {
        const index = updatedConversations.findIndex(
          ({ id }) => id === conversation.id
        );

        if (index !== -1) {
          updatedConversations[index] = conversation;
        } else {
          updatedConversations.push(conversation);
        }
      });

      return {
        ...state,
        conversations: updatedConversations,
        ...(fetchCursor !== undefined && { fetchCursor }),
      };
    },
    [updateConversation]: (state, { payload: { conversationID, ...rest } }) => {
      const updatedConversations = state.conversations.map((conversation) => {
        if (conversation.id === conversationID) {
          return {
            ...conversation,
            ...rest,
          };
        }

        return conversation;
      });

      return {
        ...state,
        conversations: updatedConversations,
      };
    },
    [addChannelTypesToConversations]: (state, { payload: { channelType } }) => {
      const updatedConversations = state.conversations.map((conversation) => {
        if (conversation.channelTypes.includes(ChannelType.ECHO)) {
          return conversation;
        }

        return {
          ...conversation,
          channelTypes: makeUniqueArray([
            ...conversation.channelTypes,
            channelType,
          ]),
        };
      });

      return {
        ...state,
        conversations: updatedConversations,
      };
    },
  },
  defaultState
);

export { createConversationReducer, conversationsReducer };
